import { useMemo, useRef, useState, type ComponentType } from 'react'
import { createRoute, useNavigate, useParams, useLocation, useSearch, Navigate, type AnyRootRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  OverviewPage,
  HuntsPage,
  TeamsPage,
  TeamDetailPage,
  TeamAccessDenied,
  MapEditorPage,
  DogsPage,
  DevicesPage,
  SettingsPage,
  AccountEmptyShell,
  PageHeader,
  HuntDetailPage,
  HuntReportPage,
  ThankYouCard,
  ReportSharingCard,
  useToast,
  polygonCentroid,
  huntDetailSearchFor,
  viewerLeadsHunt,
} from '@wehunt/ui'
import { useAuth } from '@wehunt/core-web'
import { AuthenticatedMapCatalogue } from './features/teams/AuthenticatedMapCatalogue'
import type { Hunt, Member, Team, GeoArea, GeoLine, MapEditorView, MapObject, HuntsView, PinFolder } from '@wehunt/ui'
import {
  SAMPLE_DOGS,
  SAMPLE_DEVICES,
} from './mockData'
import { useChangePassword } from './features/settings/useChangePassword'
import { hasPasswordProvider } from './features/settings/hasPasswordProvider'

/**
 * Chronicle pieces the clubhouse overview renders inline. They are injected by
 * the composition root (`apps/wehunt-web`) so clubhouse never imports the
 * chronicle domain (RULE-002). The summary type is cloned locally for the same
 * reason — clubhouse owns the shape it consumes.
 */
export interface ChronicleTeamSummary {
  id: string
  name: string
  hectares: number
  groundCount: number
}

/**
 * Feature flags the shell resolves and injects.
 *
 * The domain never reads runtime config itself: `appConfig` lives in
 * `apps/wehunt-web`, and a domain reaching into the shell would invert the
 * dependency. Injecting resolved booleans through the same seam the chronicle
 * slots already use keeps the domain a pure consumer, and keeps it testable by
 * passing a plain object.
 *
 * Optional throughout, and absence means off, so a host that has not been
 * updated behaves exactly as it did before the flag existed.
 */
export interface ClubhouseFeatures {
  /** Allow a drive to be based on a sub-area rather than the whole ground. */
  driveSubAreaScope?: boolean
  /**
   * Open an ended hunt as the report layout instead of the planning tabs.
   *
   * Off means the tabbed page, unchanged. The report does not yet cover the
   * downed-game tab's add/edit/remove, so replacing the page unconditionally
   * would withdraw a shipped feature — the flag is what lets the report land in
   * slices without doing that.
   */
  campfireHuntReport?: boolean
  /**
   * Show the team member-invite button (and its modal) on a team's Members tab.
   *
   * Off means clubhouse passes no `onInviteMember`, so the button never renders —
   * the state this page shipped in. On wires the handler that posts the invite.
   * Gated because the invite backend answers 503 until its environment has a
   * message transport, and offering a button that cannot do its job is the exact
   * complaint this work set out to fix.
   */
  teamInvite?: boolean
  /**
   * Show the "Invite guest" action on a published hunt's detail page, letting
   * the organiser invite someone outside the team by email or phone (CLUB-972).
   *
   * Off leaves the action out of `planAction`, so it never renders.
   * Gated for the same reason as {@link teamInvite}: the invite backend answers
   * 503 until its environment has a message transport, so the button must not
   * appear where it cannot do its job.
   */
  huntGuestInvite?: boolean
}

/**
 * Slots another stream fills on clubhouse's pages, injected by the composition
 * root so clubhouse never imports the owning domain (RULE-002).
 *
 * `HuntDetailHeaderAction` is campfire's "Replay hunt" button on an ended hunt. It
 * receives the hunt id and its team so the shell can build the destination, plus
 * the two facts campfire needs to decide whether a replay can exist at all — the
 * hunt's start and its attendance roster. Clubhouse supplies those because it has
 * already read them for the report; it neither knows nor cares what campfire
 * concludes from them, and the slot still renders whatever it is handed.
 */
export interface CampfireSlots {
  HuntDetailHeaderAction?: ComponentType<{
    huntId: string
    teamId?: string
    teamName?: string
    huntStartTime: string | null
    attendanceUserIds: string[]
  }>
}

export interface ChronicleSlots {
  TopStats: ComponentType<{
    teamIds: string[]
    teamsById?: Record<string, string>
    onContinueWithTeam?: (teamId: string) => void
  }>
  OverviewSection: ComponentType<{ huntsAttended?: number; hoursInField?: number }>
  ActivityPanel: ComponentType
  useTeams: () => { teams: ChronicleTeamSummary[]; isLoading: boolean }
}
import { useMember } from './features/overview/useMember'
import { useUpcomingHunts } from './features/overview/useUpcomingHunts'
import { HuntStats } from './features/hunts/HuntStats'
import { useEndedHunts, type EndedHuntDto } from './features/hunts/useEndedHunts'
import { useOngoingHunts, ongoingDtoToHunt } from './features/hunts/useOngoingHunts'
import { endedHuntsSeasonStats } from './features/hunts/endedHuntsSeasonStats'
import { upcomingDtoToHunt } from './features/hunts/upcomingHuntToHunt'
import { useDraftHunts } from './features/hunts/useDraftHunts'
import { useDeleteDraftHunt } from './features/hunts/useDraftHuntMutations'
import { useJoinHuntByCode, isValidJoinCodeFormat } from './features/hunts/useJoinHuntByCode'
import { huntsFilterFromSearch } from './features/hunts/huntsFilterFromSearch'
import { draftDtoToHunt } from './features/hunts/draftHuntToHunt'
import { composeHuntsList } from './features/hunts/composeHuntsList'
import { useMyGroups } from './features/teams/useMyGroups'
import { useThankYouInfo, useSendThankYou } from './features/hunts/useThankYou'
import { useReportSharingInfo, useSetReportSharing } from './features/hunts/useReportSharing'
import { groupToTeam, EMPTY_TEAM_MAP } from './features/teams/groupToTeam'
import { useCreateGroup, teamCreatedNavTarget } from './features/teams/useCreateGroup'
import { useJoinTeamByCode } from './features/teams/useJoinTeamByCode'
import { useDeleteGroup } from './features/teams/useDeleteGroup'
import { useLeaveGroup } from './features/teams/useLeaveGroup'
import { useGroupMembers, type GroupMemberDto } from './features/teams/useGroupMembers'
import { memberRoleIdFromWire, isOwnerWireRole } from './features/teams/memberRoleFromWire'
import { useUpdateMemberRole } from './features/teams/useUpdateMemberRole'
import { useRemoveMember } from './features/teams/useRemoveMember'
import { useGenerateJoinCode } from './features/teams/useGenerateJoinCode'
import { useClearJoinCode } from './features/teams/useClearJoinCode'
import { useRenameGroup } from './features/teams/useRenameGroup'
import { useSetTeamPhoto, useRemoveTeamPhoto } from './features/teams/useTeamPhoto'
import { useGroupMapPins } from './features/teams/useGroupMapPins'
import { useMyMapPins, useMyPinFolders } from './features/teams/useMyPinCollection'
import { useGroupPinFolders } from './features/teams/useGroupPinFolders'
import { useGroupCameraPins } from './features/teams/useGroupCameraPins'
import { useMapPinVisibility } from './features/teams/useMapPinVisibility'
import { useGroupMapLines } from './features/teams/useGroupMapLines'
import { useGroupHuntingGrounds } from './features/teams/useGroupHuntingGrounds'
import { saveTeamMap } from './features/teams/saveTeamMap'
import { loadCopyableTeamMap } from './features/teams/loadCopyableTeamMap'
import { importOmaRiistaArea } from './features/teams/api/omaRiistaImportApi'
import { importSveaskogArea } from './features/teams/api/sveaskogImportApi'
import { connectReviiri, fetchReviiriClubs, importReviiriMap } from './features/teams/api/reviiriApi'
import { fetchPrintProbe } from './features/teams/api/printProbeApi'
import { useGroupPhotos } from './features/teams/useGroupPhotos'
import { useGroupHunts, groupUpcomingDtoToHunt } from './features/hunts/useGroupHunts'
import { useTeamHunts } from './features/hunts/useTeamHunts'
import { useGroupDocuments } from './features/teams/useGroupDocuments'
import { useDocumentOperations } from './features/teams/useDocumentOperations'
import { useInviteMember } from './features/teams/useInviteMember'
import { parseInviteInput } from './features/teams/inviteInput'
import { isRecipientRejected } from './features/teams/inviteErrors'
import { ApiError } from './shared/api'
import { useUpcomingHuntDetail } from './features/hunts/useUpcomingHuntDetail'
import { HuntGuestInviteAction } from './features/hunts/HuntGuestInviteAction'
import { HuntPlanDogs } from './features/hunts/HuntPlanDogs'
import { HuntPlanDeleteAction } from './features/hunts/HuntPlanDeleteAction'
import { attachedTeamDocuments } from './features/hunts/attachedTeamDocuments'
import { endedHuntToOpen, useRespondToHuntInvite } from './features/hunts/useRespondToHuntInvite'
import {
  useHuntGamePins,
  useCreateHuntGamePin,
  useUpdateHuntGamePin,
  useDeleteHuntGamePin,
} from './features/hunts/useHuntGamePins'
import { huntDetailSearch } from './features/hunts/huntDetailSearch'
import { useGameNamesCatalog } from './features/hunts/plan/useGameNamesCatalog'
import { TeamStatisticsContainer } from './features/statistics/TeamStatisticsContainer'
import { PlanHuntPage } from './features/hunts/plan/PlanHuntPage'

function chronicleTeamToTeam(t: ChronicleTeamSummary): Team {
  return {
    id: t.id,
    name: t.name,
    role: 'member',
    members: 0,
    area: '',
    hectares: t.hectares,
    nextHunt: '',
    joinCode: '',
    map: EMPTY_TEAM_MAP,
  }
}

function groupMemberDtoToMember(dto: GroupMemberDto, currentUserId?: string): Member {
  // Both stored vocabularies are read — see memberRoleFromWire. Inline, this
  // treated mobile's `member` as view-only, which mislabelled nearly every
  // member of a migrated team.
  const isOwner = isOwnerWireRole(dto.role)
  const roleId = memberRoleIdFromWire(dto.role)
  return {
    id: dto.id,
    name: dto.name,
    roleId,
    avatar: dto.avatar,
    photo: dto.imageUrl,
    you: dto.id === currentUserId,
    owner: isOwner,
  }
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function endedDtoToHunt(dto: EndedHuntDto): Hunt {
  const start = new Date(dto.startTime)
  const end = dto.endTime ? new Date(dto.endTime) : start
  const startIso = isoDate(start)
  const endIso = isoDate(end)
  const quarry = dto.huntableGames.length > 0 ? dto.huntableGames.join(', ') : '—'
  return {
    id: dto.id,
    date: startIso,
    endDate: dto.endTime ? endIso : undefined,
    start: `${pad2(start.getHours())}:${pad2(start.getMinutes())}`,
    end: `${pad2(end.getHours())}:${pad2(end.getMinutes())}`,
    team: dto.groupName,
    teamId: dto.groupId,
    quarry,
    location: dto.huntName,
    participants: dto.participantCount,
    dogs: [],
    status: 'ended',
  }
}

function HuntsRoute() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: upcoming } = useUpcomingHunts()
  const { data: ongoing } = useOngoingHunts()
  const { data: ended } = useEndedHunts()
  const { data: drafts } = useDraftHunts()
  const { data: groups } = useMyGroups()
  const deleteDraft = useDeleteDraftHunt()
  const joinByCode = useJoinHuntByCode()

  const upcomingHunts = (upcoming?.hunts ?? []).map(upcomingDtoToHunt)
  const ongoingHunts = (ongoing?.hunts ?? []).map(ongoingDtoToHunt)
  const endedHunts = (ended?.hunts ?? []).map(endedDtoToHunt)
  const draftHunts = (drafts?.hunts ?? []).map(draftDtoToHunt)

  // CLUB-272 — also surface hunts from the user's teams that they are NOT part
  // of (not invited), so they can see what the team has planned/ongoing.
  const groupIds = (groups?.groups ?? []).map((g) => g.id)
  const { hunts: teamHunts } = useTeamHunts(groupIds)
  // The caller's own hunts (invited upcoming + live) already appear via the
  // my-scoped feeds; drop them from the team feed so a hunt the user is on
  // isn't listed twice.
  const myHuntIds = new Set([...upcomingHunts, ...ongoingHunts].map((h) => h.id))
  const notInvitedTeamHunts = teamHunts.filter((h) => !myHuntIds.has(h.id))

  const hunts: Hunt[] = composeHuntsList({
    upcoming: upcomingHunts,
    ongoing: ongoingHunts,
    notInvitedTeam: notInvitedTeamHunts,
    ended: endedHunts,
    drafts: draftHunts,
  })
  const teams = (groups?.groups ?? []).map(groupToTeam)

  // Filter and view live in the URL so a stat tile is a real navigation: Back
  // returns to whatever the hunter was looking at, and a filtered list can be
  // linked to. An unrecognised value falls back rather than blanking the page.
  const search = new URLSearchParams(location.search)
  const filter = huntsFilterFromSearch(search.get('filter'))
  const view: HuntsView = search.get('view') === 'list' ? 'list' : 'month'

  return (
    <HuntsPage
      hunts={hunts}
      teams={teams}
      filter={filter}
      view={view}
      onBrowseChange={(next) => {
        // Defaults stay out of the URL so the plain /hunts link keeps working
        // and doesn't accumulate noise.
        const params: Record<string, string> = {}
        if (next.filter !== 'all') params.filter = next.filter
        if (next.view !== 'month') params.view = next.view
        void navigate({ to: '/hunts', search: params })
      }}
      onPlanHunt={(draftId?: string, groupId?: string) =>
        navigate({ to: '/hunts/plan', search: draftId ? { draftId, ...(groupId ? { groupId } : {}) } : {} })}
      onPlanHuntForDate={(date) => navigate({ to: '/hunts/plan', search: { date } })}
      onJoinByCode={async (code) => {
        const outcome = await joinByCode.mutateAsync(code)
        // The hook throws for 'failed' | 'expired' | 'ambiguous', so by the
        // time this resolves the outcome is always 'joined' or 'viewed'.
        return outcome as 'joined' | 'viewed'
      }}
      validateJoinCode={isValidJoinCodeFormat}
      onDiscardDraft={(h) => {
        // The Drafts rail confirms before calling this; here we just delete.
        if (h.teamId) deleteDraft.mutate({ groupId: h.teamId, draftId: h.id })
      }}
      onOpenHunt={(id) => {
        const hunt = hunts.find((h) => h.id === id)
        const search = hunt ? huntDetailSearchFor(hunt, '/hunts') : { from: '/hunts' }
        void navigate({ to: '/hunts/$id', params: { id }, search })
      }}
      statsSection={(actions) => (
        <HuntStats
          onViewInvitations={actions.showPending}
          onViewUpcoming={actions.showUpcoming}
          onViewEnded={actions.showEnded}
        />
      )}
    />
  )
}

// PlanHuntRoute is deliberately NOT declared here: it needs the injected
// feature flags, so it lives inside buildClubhouseRoutes where it can close
// over them — the same reason OverviewRoute is defined there rather than at
// module scope.

/**
 * Fills HuntReportPage's `thankYou` slot: the "Thank the participants" card and its
 * send flow, wired to the API. Renders nothing until the info query answers, so a
 * plain participant (whose GET is 403) never sees the card; and nothing for an
 * ordinary club, since thanking participants is an estate-team feature. The `onSend`
 * carries the organiser's locale through so the built-in templates are composed in it.
 */
function HuntThankYou({ groupId, huntId, locale }: { groupId?: string; huntId: string; locale: string }) {
  const { t } = useTranslation('huntInvitation')
  const info = useThankYouInfo(groupId, huntId, locale)
  const send = useSendThankYou(groupId, huntId)
  if (!info.data || !info.data.isEstate) return null
  return (
    <ThankYouCard
      sentAt={info.data.sentAt}
      participantNames={info.data.participantNames}
      templates={info.data.templates}
      onSend={async (req) => {
        try {
          return await send.mutateAsync({ ...req, locale })
        } catch (e) {
          // The API answers { error, code }, so ApiError.message is only "HTTP 409".
          // The card is presentation-only and must never show that — translate the
          // known codes here, where the API contract is known.
          const code = e instanceof ApiError && typeof e.problem.code === 'string' ? e.problem.code : null
          const key =
            code === 'thank_you.already_sent' ? 'thankYou.errorAlreadySent'
            : code === 'thank_you.delivery_not_configured' ? 'thankYou.errorNotConfigured'
            : 'thankYou.error'
          throw new Error(t(key))
        }
      }}
    />
  )
}

/**
 * Fills HuntReportPage's `reportSharing` slot: the "What participants can see" card
 * and its share toggle, wired to the API. Renders nothing until the info query
 * answers, so a plain participant (whose GET is 403) never sees the card; and nothing
 * for an ordinary club, since sharing an ended report is an estate-team feature.
 */
function HuntReportSharing({ groupId, huntId }: { groupId?: string; huntId: string }) {
  const { t } = useTranslation('huntInvitation')
  const info = useReportSharingInfo(groupId, huntId)
  const set = useSetReportSharing(groupId, huntId)
  if (!info.data || !info.data.isEstate) return null
  return (
    <ReportSharingCard
      shared={info.data.isShared}
      participantNames={info.data.participantNames}
      onToggle={async (next) => {
        try {
          await set.mutateAsync({ shared: next })
        } catch (e) {
          // The API answers { error, code }, so ApiError.message is only "HTTP 409".
          // The card is presentation-only and must never show that — translate the
          // known codes here, where the API contract is known, and rethrow so the
          // card keeps the toggle on the server's confirmed state.
          const code = e instanceof ApiError && typeof e.problem.code === 'string' ? e.problem.code : null
          const key =
            code === 'report_sharing.estate_only' ? 'reportSharing.errorEstateOnly'
            : code === 'report_sharing.hunt_not_ended' ? 'reportSharing.errorNotEnded'
            : 'reportSharing.error'
          throw new Error(t(key))
        }
      }}
    />
  )
}

function HuntDetailRoute({ campfire, features }: { campfire: CampfireSlots; features: ClubhouseFeatures }) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { id } = useParams({ strict: false }) as { id: string }
  // Read through the router's own search codec, never `URLSearchParams` on the
  // raw query string: the router JSON-encodes `ended`/`ongoing` in the URL, so
  // decoding them by hand silently reads every in-app link's flag as off. See
  // `huntDetailSearch`.
  // The team's own border colour, for the report map below. Read from the same
  // my-groups query every other surface reads it from, so a colour change invalidating
  // that query reaches this map too rather than needing its own cache entry.
  const { data: huntDetailGroups } = useMyGroups()
  const { teamId, from, ended, ongoing } = huntDetailSearch(
    useSearch({ strict: false }) as Record<string, unknown>,
  )

  const { data: detail, isLoading } = useUpcomingHuntDetail(teamId, id)
  const respond = useRespondToHuntInvite(teamId, id)

  // Downed-game reporting (ended hunts). Hooks run unconditionally to satisfy
  // the rules of hooks; the tab that uses this data only renders when `ended`.
  const { state: authState } = useAuth()
  const currentUserId = authState.status === 'signed-in' ? authState.user.uid : undefined
  const { data: gamePinsData, loadFailed: gamePinsLoadFailed } = useHuntGamePins(teamId, id, ended)
  const createGamePin = useCreateHuntGamePin(teamId, id)
  const updateGamePin = useUpdateHuntGamePin(teamId, id)
  const deleteGamePin = useDeleteHuntGamePin(teamId, id)
  const { catalog: speciesCatalog } = useGameNamesCatalog()
  // The hunting-ground geometry is only needed to seed the default pin location
  // when adding on an ended hunt — passing an empty groupId when not ended keeps
  // the hook called (rules of hooks) but disables its fetch.
  const huntingGrounds = useGroupHuntingGrounds(ended ? (teamId ?? '') : '')

  // Boundary points for the centroid. Keyed on the raw hunting-grounds query
  // data (`rawAreas`), which react-query keeps a stable reference to between
  // renders — unlike the hook's derived `boundaryAreas`, which is a fresh array
  // every render and would defeat the memo below.
  const boundaryPts = useMemo(
    () =>
      huntingGrounds.rawAreas
        .filter((a) => a.areaType === 'hunt')
        // Every ring, from whichever shape the API sent. A centroid over the first
        // part of a multi-part ground sits inside that part rather than in the
        // middle of the ground, which is where a new pin would then land.
        .flatMap((a) => (a.rings?.length ? a.rings.flat() : (a.coordinates ?? [])))
        .map((c) => ({ lat: c.latitude, lon: c.longitude })),
    [huntingGrounds.rawAreas],
  )

  // Default location for a new pin: the hunting-ground centroid. If there is no
  // boundary yet, fall back to the centroid of the existing pins; if there are
  // neither, leave it undefined so the UI blocks Add with a hint (a pin must
  // carry a location).
  const defaultLocation = useMemo(() => {
    // polygonCentroid answers null for an empty set rather than the origin, so
    // undefined stays the single way this says it has no default - the UI already
    // blocks Add on it with a hint.
    return (
      polygonCentroid(boundaryPts) ??
      polygonCentroid((gamePinsData?.pins ?? []).map((p) => p.location)) ??
      undefined
    )
  }, [boundaryPts, gamePinsData])

  /** This team's border colour, for the report map below. */
  const reportTeamBorderColor = (huntDetailGroups?.groups ?? []).find(
    (g) => g.id === teamId,
  )?.borderColor

  // The hunt references team documents by id, so their names and storage paths
  // come from the team's document listing rather than from the hunt itself.
  const { docTree } = useGroupDocuments(teamId ?? '')
  const teamDocuments = useMemo(
    () => attachedTeamDocuments(teamId ?? '', docTree, detail?.attachmentDocumentIds),
    [teamId, docTree, detail?.attachmentDocumentIds],
  )

  // Resolve a storage path → download URL when the user clicks an
  // attachment chip. Lazy import keeps the Firebase Storage SDK out of the
  // synchronous bundle path; the click is async anyway.
  const resolveAttachmentUrl = async (storagePath: string): Promise<string> => {
    const [{ getStorage, ref, getDownloadURL }] = await Promise.all([
      import('firebase/storage'),
    ])
    return getDownloadURL(ref(getStorage(), storagePath))
  }

  const goBack = () => {
    const url = new URL(from, window.location.origin)
    const search = Object.fromEntries(url.searchParams.entries())
    void navigate({ to: url.pathname as '/', search: Object.keys(search).length > 0 ? search : undefined })
  }

  if (!teamId) {
    return (
      <div style={{ padding: '0 0 40px' }}>
        <PageHeader
          title={t('routes.huntDetail.title')}
          actions={
            <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
              {t('routes.huntDetail.back')}
            </button>
          }
        />
        <p style={{ color: 'var(--nl-outline)', fontSize: 14 }}>{t('routes.huntDetail.unavailable')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0 0 40px' }}>
        <PageHeader
          title={t('routes.huntDetail.title')}
          actions={
            <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
              {t('routes.huntDetail.back')}
            </button>
          }
        />
        <p style={{ color: 'var(--nl-outline)', fontSize: 14 }}>{t('routes.huntDetail.loading')}</p>
      </div>
    )
  }

  if (!detail) {
    return (
      <div style={{ padding: '0 0 40px' }}>
        <PageHeader
          title={t('routes.huntDetail.notFoundTitle')}
          actions={
            <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
              {t('routes.huntDetail.back')}
            </button>
          }
        />
        <p style={{ color: 'var(--nl-outline)', fontSize: 14 }}>{t('routes.huntDetail.notFoundBody')}</p>
      </div>
    )
  }

  const HeaderAction = campfire.HuntDetailHeaderAction
  const headerAction =
    HeaderAction === undefined ? undefined : (
      <HeaderAction
        huntId={id}
        teamId={teamId}
        teamName={detail.groupName}
        huntStartTime={detail.startTime}
        // On an ended hunt this list IS the `hunters` roster — the record of who
        // was in the field — not the plan's invite list. That is exactly the
        // roster the archive service authorises against.
        attendanceUserIds={detail.participants.map((p) => p.userId)}
      />
    )

  // An ended hunt is a report, not a plan: the tabs exist to work through an
  // upcoming hunt (invitation, drives, RSVP) and none of that is actionable once
  // it is over. Same route, so existing links keep working.
  if (ended && features.campfireHuntReport === true) {
    return (
      <HuntReportPage
        huntName={detail.huntName}
        teamName={detail.groupName}
        startTime={detail.startTime}
        endTime={detail.endTime}
        participants={detail.participants}
        gamePins={gamePinsData?.pins ?? []}
        boundaryAreas={huntingGrounds.boundaryAreas}
        // The report map is one of the surfaces the team's border colour is supposed to
        // reach. HuntReportPage declared and forwarded the prop all the way into the
        // ground layer, and this - the only production mount - never supplied it, so the
        // report rendered the domain default while every other surface showed the team's
        // choice.
        teamBorderColor={reportTeamBorderColor}
        subAreaPolygons={huntingGrounds.subAreaPolygons}
        forbiddenPolygons={huntingGrounds.forbiddenPolygons}
        gamePinsLoadFailed={gamePinsLoadFailed}
        dogs={detail.dogs}
        comment={detail.comment}
        manHours={detail.manHours}
        currentUserId={currentUserId}
        // The same handlers the tabbed page gets. The report replaces that page
        // when its flag is on, so without these it would withdraw the add / edit /
        // remove of downed game that shipped with CLUB-545.
        canManageAll={gamePinsData?.canManageAll ?? false}
        defaultLocation={defaultLocation}
        speciesCatalog={speciesCatalog}
        onCreateGamePin={async (input) => {
          await createGamePin.mutateAsync(input)
        }}
        onUpdateGamePin={async (pinId, input) => {
          await updateGamePin.mutateAsync({ id: pinId, input })
        }}
        onDeleteGamePin={async (pinId) => {
          await deleteGamePin.mutateAsync(pinId)
        }}
        headerAction={headerAction}
        thankYou={<HuntThankYou groupId={teamId} huntId={id} locale={i18n.language} />}
        reportSharing={<HuntReportSharing groupId={teamId} huntId={id} />}
        onBack={goBack}
      />
    )
  }

  return (
    <HuntDetailPage
      huntId={id}
      detail={detail}
      ended={ended}
      ongoing={ongoing}
      onClose={goBack}
      headerAction={headerAction}
      planAction={
        // Delete is for the plan's leaders only, the rule deletehuntplan
        // enforces; the slot itself also opens to the plan's creator.
        <>
          {viewerLeadsHunt(detail, currentUserId) && (
            <HuntPlanDeleteAction groupId={teamId} huntPlanId={id} huntName={detail.huntName} onDeleted={goBack} />
          )}
          {features.huntGuestInvite && <HuntGuestInviteAction groupId={teamId} huntPlanId={id} />}
        </>
      }
      onNavigate={(path) => navigate({ to: path as '/' })}
      onResolveAttachmentUrl={resolveAttachmentUrl}
      teamDocuments={teamDocuments}
      // Dogs are managed on a plan AND on an ongoing hunt, but not a finished
      // one. `detail.ended` is the authoritative gate — the write path then
      // follows the lifecycle (in HuntPlanDogs): a started hunt writes the live
      // hunt's dogs, a plan registers them on the plan. On an ongoing hunt (read
      // by hunt id) `detail.id` is the hunt id; on a started plan `detail.huntId`
      // is. The URL ended/ongoing flags are unreliable, so this uses the API's
      // authoritative lifecycle instead.
      dogsSection={
        !detail.ended ? (
          <HuntPlanDogs
            groupId={teamId}
            huntPlanId={id}
            registered={detail.dogs ?? []}
            isOrganizer={detail.isOrganizer}
            started={detail.started ?? false}
            liveHuntId={detail.huntId ?? detail.id}
          />
        ) : undefined
      }
      onRespond={
        !ended && !ongoing && detail.inviteStatus != null
          ? async (status, responseText) => {
              const result = await respond.mutateAsync({ status, responseText })
              // See endedHuntToOpen for when this fires and why it is guarded.
              // The id is the HUNT's, not this plan's; the ended detail route
              // resolves it against the `hunts` collection.
              const endedHuntId = endedHuntToOpen(status, result)
              if (endedHuntId) {
                const search = huntDetailSearchFor({ teamId, status: 'ended' }, from)
                void navigate({ to: '/hunts/$id', params: { id: endedHuntId }, search })
              }
            }
          : undefined
      }
      currentUserId={currentUserId}
      gamePins={gamePinsData?.pins}
      gamePinsLoadFailed={gamePinsLoadFailed}
      canManageAll={gamePinsData?.canManageAll ?? false}
      defaultLocation={defaultLocation}
      speciesCatalog={speciesCatalog}
      onCreateGamePin={async (input) => {
        await createGamePin.mutateAsync(input)
      }}
      onUpdateGamePin={async (pinId, input) => {
        await updateGamePin.mutateAsync({ id: pinId, input })
      }}
      onDeleteGamePin={async (pinId) => {
        await deleteGamePin.mutateAsync(pinId)
      }}
    />
  )
}

function TeamsRoute() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Three states have to be told apart, because an empty `teams` array is not
  // enough on its own: a failed request, an account with genuinely no teams, and
  // a request still in flight all map through `?? []` to the same empty grid
  // under "you are a member of 0 teams" — which users reported as their teams
  // having been deleted.
  //   - `isPending` (no data and no error yet) is the still-loading state.
  //   - a failure is only a HARD failure when there is nothing to show: with a
  //     cached list in hand (staleTime is 0, so every visit refetches), a
  //     transient error on the background refetch must NOT replace the list the
  //     user can still see with an error card.
  const { data: groups, isError, isPending, refetch } = useMyGroups()
  const createGroup = useCreateGroup()
  const joinTeam = useJoinTeamByCode()
  const { showToast } = useToast()
  const teams = (groups?.groups ?? []).map(groupToTeam).sort((a, b) => a.name.localeCompare(b.name))
  return (
    <TeamsPage
      teams={teams}
      loadFailed={isError && !groups}
      loading={isPending}
      onRetry={() => void refetch()}
      onCreateTeam={async (name) => {
        // CLUB-180: on success, go straight into the new team (Members tab).
        // A rejection propagates to NewTeamDialog, which keeps the dialog open
        // with the error — so on failure we never navigate.
        const created = await createGroup.mutateAsync(name)
        void navigate(teamCreatedNavTarget(created))
      }}
      onJoinTeam={async (code) => {
        // CLUB-177: join by code. A rejection propagates to JoinTeamDialog, which
        // keeps the dialog open with the error. On success the my-groups list is
        // invalidated (in the hook) so the joined team appears; confirm via toast.
        await joinTeam.mutateAsync(code)
        showToast(t('routes.team.joined'))
      }}
    />
  )
}

function TeamDetailRoute({ features }: { features: ClubhouseFeatures }) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  const { teamId } = useParams({ strict: false }) as { teamId: string }
  const { data: groups } = useMyGroups()
  // Mapped once per answer. `baseTeam` is the seed of the map object below, and
  // a fresh one each render re-frames the team map — see the comment there.
  const teams = useMemo(() => (groups?.groups ?? []).map(groupToTeam), [groups])
  const baseTeam = teams.find((t) => t.id === teamId)
  const location = useLocation()
  const tab = new URLSearchParams(location.search).get('tab') ?? undefined
  const { data: membersData } = useGroupMembers(teamId)
  const members = (membersData?.members ?? [])
    .filter((m) => m.name.toLowerCase().trim() !== 'deleted user')
    .map((m) => groupMemberDtoToMember(m, uid))
    .sort((a, b) => a.name.localeCompare(b.name))
  const mapPins = useGroupMapPins(teamId)
  // The team's trail cameras, as camera pins. They are NOT part of `mapPins`:
  // a camera's position lives in the Burrel database, and no client writes it
  // into the shared `mapPins` collection — which is why the map here drew no
  // camera pins at all while the app drew one per camera. Read-only, so they are
  // merged into this page's map and deliberately never into the editor's, whose
  // save diffs its objects against the loaded `mapPins`.
  const cameraPins = useGroupCameraPins(teamId)
  // The signed-in hunter's OWN pins, drawn on this team's map. Private by
  // construction, not by a filter here: the route behind this hook resolves the
  // owner from the caller's token, so no other member's browser can load them.
  // Merged into the read-only map only — never into the editor's object list,
  // whose save diffs against the team's pins and whose writes the team endpoints
  // refuse for a pin the team does not own.
  const myPins = useMyMapPins()
  // Folder NAMES for both collections. The pins already carry the folder id they
  // are filed under; these two lists are what turn those ids into a structure the
  // Map tab can show, and their order is the order both platforms display.
  const { folders: teamPinFolders } = useGroupPinFolders(teamId)
  const myPinFolders = useMyPinFolders()
  const huntingGrounds = useGroupHuntingGrounds(teamId)
  // Every kind (line/arrow/trail), unfiltered: what to draw is the page's
  // decision, and filtering here would hide hunter-recorded trails from the
  // team map. Unlike the editor route this does NOT gate the render on the
  // query resolving — there is no save to diff against here, so late lines
  // simply appear, and blocking the whole team page (members, photos,
  // documents) on a lines fetch would trade an invisible annotation for a
  // blank page.
  const { lines: mapLines } = useGroupMapLines(teamId)
  const { data: photosData } = useGroupPhotos(teamId)
  const { data: groupHunts } = useGroupHunts(teamId)
  const { docTree, refresh } = useGroupDocuments(teamId)
  const ops = useDocumentOperations(teamId, refresh)
  const deleteGroup = useDeleteGroup()
  const leaveGroup = useLeaveGroup()
  const updateMemberRole = useUpdateMemberRole(teamId)
  const removeMember = useRemoveMember(teamId)
  const generateJoinCode = useGenerateJoinCode(teamId)
  const clearJoinCode = useClearJoinCode(teamId)
  const renameGroup = useRenameGroup(teamId)
  const inviteMember = useInviteMember(teamId)
  const setTeamPhoto = useSetTeamPhoto(teamId)
  const removeTeamPhoto = useRemoveTeamPhoto(teamId)
  const { showToast } = useToast()
  // Per-viewer display state for the Map tab's pin collection: which pins and
  // lines this hunter is choosing not to draw. Held here because the panel
  // takes its visibility controls from its host, and this route is the host.
  // Saved on the hunter's own user document, where the app keeps the same
  // choice, so it changes nothing for the rest of the team.
  const teamLineIds = useMemo(() => mapLines.map((l) => l.id), [mapLines])
  const pinVisibility = useMapPinVisibility(teamId, teamLineIds, () =>
    showToast(t('routes.team.visibilityError')),
  )
  const pinCollection = useMemo(() => ({ visibility: pinVisibility }), [pinVisibility])

  const upcomingHunts = (groupHunts?.upcoming ?? []).map(groupUpcomingDtoToHunt)
  const ongoingHunts = (groupHunts?.ongoing ?? []).map(ongoingDtoToHunt)
  const endedHunts = (groupHunts?.ended ?? []).map(endedDtoToHunt)
  const hunts: Hunt[] = [...ongoingHunts, ...upcomingHunts, ...endedHunts]

  // MEMOIZED, and the identity is the load-bearing part. `TeamMapView` treats a
  // new `map` object as a different map: it remounts the underlying map and
  // re-applies the fit bounds, which is right when you open another team's
  // ground and wrong for anything else. Rebuilt inline, this object was new on
  // every render of the route, so any re-render at all — a background refetch,
  // a toast, and once the pin panel got its show/hide controls, every single
  // toggle — threw the reader back to the whole-ground view and made them zoom
  // in again. The dependencies are all per-answer values now (see the hooks),
  // so this changes when the data does and not before.
  const team = useMemo(
    () =>
      baseTeam
        ? {
            ...baseTeam,
            map: {
              ...baseTeam.map,
              boundaryAreas: huntingGrounds.boundaryAreas,
              subAreaPolygons: huntingGrounds.subAreaPolygons,
              forbiddenPolygons: huntingGrounds.forbiddenPolygons,
              objects: [...mapPins, ...cameraPins, ...myPins],
              // Tagged by collection on the way in: a folder id is unique only
              // within its own collection, so the consumer has to be able to tell a
              // team folder from a private one carrying the same id.
              pinFolders: [
                ...teamPinFolders.map((f) => ({ ...f, private: false })),
                ...myPinFolders.map((f) => ({ ...f, private: true })),
              ],
              lines: mapLines,
            },
          }
        : undefined,
    [baseTeam, huntingGrounds, mapPins, cameraPins, myPins, teamPinFolders, myPinFolders, mapLines],
  )

  if (!team) {
    // Groups still loading — nothing to show yet (avoids a flash of "no access").
    if (groups === undefined) return null
    // Groups loaded but this team isn't one of them → the user isn't a member.
    // Team data is 403'd server-side; show the matching no-access state instead
    // of a blank page (a shared deep link must not expose the team).
    return <TeamAccessDenied onBack={() => navigate({ to: '/teams' })} />
  }

  return (
    <TeamDetailPage
      team={team}
      // The server-rendered PDF print exchanges this for a print session; fetched
      // per call rather than captured, since a print may be started long after the
      // page loaded and a captured token would have expired.
      getAuthToken={async () =>
        state.status === 'signed-in' ? await state.user.getIdToken() : null
      }
      // What the print server would use for a sheet centred here, so the print
      // surface can draw the frame the server will actually compose. Answered by
      // the Estate API's Tanger passthrough — web never calls Tanger directly.
      // Out of session the lookup rejects: the endpoint is authenticated, and no
      // frame is a better answer than a made-up one.
      getPrintProbe={async (center) => {
        if (state.status !== 'signed-in') throw new Error('print probe: signed out')
        return await fetchPrintProbe(center.lat, center.lon, await state.user.getIdToken())
      }}
      teams={teams}
      hunts={hunts}
      tab={tab}
      members={membersData !== undefined ? members : undefined}
      photos={photosData?.photos}
            photosTruncated={photosData?.truncated}
      docTree={docTree}
      ops={ops}
      // Fills the Statistics tab slot with the data-connected surface (period
      // picker + totals + by-species). Presence of this section is what makes the
      // tab appear; the container owns the period state, the stats fetch and the
      // game-name catalog for localized species labels.
      statisticsSection={<TeamStatisticsContainer groupId={team.id} />}
      pinCollection={pinCollection}
      // Gated by the teamInvite flag: passing no onInviteMember keeps
      // TeamDetailPage's invite button hidden, which is the state this page
      // shipped in. The flag is on where the invite transport is being brought up
      // (staging) and off in production until Notifications:Provider is "real"
      // there — the button answering 503 for an admin who has already typed an
      // address is the exact complaint this work set out to fix.
      //
      // inviteUnavailable degrades that 503 to "share the join code instead" for
      // whatever window exists between the flag flipping on and the transport
      // going live; the three routes.team.invite* keys are translated in every
      // locale.
      onInviteMember={
        features.teamInvite
          ? async (raw) => {
              const parsed = parseInviteInput(raw)
              if (!parsed.ok) return { ok: false, error: parsed.error }
              try {
                await inviteMember.mutateAsync({ ...parsed.invite, locale: i18n.language })
                showToast(t('routes.team.inviteSent', { recipient: parsed.invite.value }))
                return { ok: true }
              } catch (e) {
                if (e instanceof ApiError && e.status === 503) {
                  return { ok: false, error: t('routes.team.inviteUnavailable') }
                }
                if (isRecipientRejected(e)) {
                  return { ok: false, error: t('routes.team.inviteRecipientRejected') }
                }
                // The button is shown only to the owner and administrators, so a
                // 403 means the role changed while the page was open; say why
                // rather than inviting a retry that will fail the same way.
                if (e instanceof ApiError && e.status === 403) {
                  return { ok: false, error: t('routes.team.inviteForbidden') }
                }
                return { ok: false, error: t('routes.team.inviteError') }
              }
            }
          : undefined
      }
      onDeleteTeam={
        // Owner-only delete. The UI only renders the action for the Owner and
        // confirms via type-to-confirm; the backend enforces owner-only (an ERROR
        // envelope for non-owners). On success drop back to the Teams list with
        // feedback. A failure is left to reject: the confirmation stays open and
        // shows it there, next to the name the owner typed, rather than in a
        // toast over a modal that has already closed.
        team.role === 'owner'
          ? async () => {
              await deleteGroup.mutateAsync(team.id)
              showToast(t('routes.team.deleted', { name: team.name }))
              void navigate({ to: '/teams' })
            }
          : undefined
      }
      onLeaveTeam={
        // Everyone except the Owner may leave; the Owner has to transfer
        // ownership first (the backend refuses to remove an owner, and the UI
        // hides the action for them). `uid` is the leaver's own id, which is
        // what makes this a self-removal the backend allows without any role.
        // On success drop back to the Teams list — the user can no longer read
        // this team, so staying on the page would show a no-access state.
        team.role !== 'owner' && uid
          ? async () => {
              try {
                await leaveGroup.mutateAsync({ groupId: team.id, userId: uid })
                showToast(t('routes.team.left', { name: team.name }))
                void navigate({ to: '/teams' })
              } catch (e) {
                showToast(e instanceof Error ? e.message : t('routes.team.leaveError'))
              }
            }
          : undefined
      }
      onChangeMemberRole={
        // CLUB-174: persist a role change. The UI updates optimistically and
        // reverts if this rejects; the backend enforces who may change roles.
        async (memberId, roleId) => {
          try {
            await updateMemberRole.mutateAsync({ memberId, role: roleId })
            showToast(t('routes.team.roleUpdated'))
          } catch (e) {
            showToast(e instanceof Error ? e.message : t('routes.team.roleError'))
            throw e
          }
        }
      }
      onRemoveMember={
        // Persist a member removal (admin/owner "kick") via the
        // removeUserFromGroup callable. The UI removes optimistically and
        // restores the member if this rejects (it re-throws); the backend
        // enforces who may remove whom.
        async (memberId) => {
          try {
            await removeMember.mutateAsync({ userId: memberId })
            showToast(t('routes.team.memberRemoved'))
          } catch (e) {
            showToast(e instanceof Error ? e.message : t('routes.team.removeMemberError'))
            throw e
          }
        }
      }
      onGenerateJoinCode={
        // CLUB-500: Owner/Administrator-only generate/regenerate. The UI only
        // renders the control for managers; the backend enforces (403 otherwise).
        team.role === 'owner' || team.role === 'admin'
          ? async () => {
              try {
                await generateJoinCode.mutateAsync()
                showToast(t('routes.team.joinCodeGenerated'))
              } catch (e) {
                showToast(e instanceof Error ? e.message : t('routes.team.joinCodeError'))
              }
            }
          : undefined
      }
      onDeleteJoinCode={
        // CLUB-500: Owner/Administrator-only delete — revokes joining until a new
        // code is generated. Backend enforces manager-only.
        team.role === 'owner' || team.role === 'admin'
          ? async () => {
              try {
                await clearJoinCode.mutateAsync()
                showToast(t('routes.team.joinCodeDeleted'))
              } catch (e) {
                showToast(e instanceof Error ? e.message : t('routes.team.joinCodeError'))
              }
            }
          : undefined
      }
      // Rename is owner-only on the server; the Settings tab hides the card for
      // every other role. Withholding the prop for non-owners would disable a
      // control they cannot see anyway, so it is passed unconditionally and the
      // server stays the enforcement point.
      onRenameTeam={async (name) => {
        try {
          await renameGroup.mutateAsync(name)
          showToast(t('routes.team.renamed'))
        } catch {
          showToast(t('routes.team.renameError'))
        }
      }}
      onChangeTeamPhoto={async (file) => {
        try {
          await setTeamPhoto.mutateAsync(file)
          showToast(t('routes.team.photoUpdated'))
        } catch {
          showToast(t('routes.team.photoError'))
        }
      }}
      onRemoveTeamPhoto={async () => {
        try {
          await removeTeamPhoto.mutateAsync()
          showToast(t('routes.team.photoRemoved'))
        } catch {
          showToast(t('routes.team.photoError'))
        }
      }}
      onBack={() => navigate({ to: '/teams' })}
      onNavigate={(path) => {
        const url = new URL(path, window.location.origin)
        const search = Object.fromEntries(url.searchParams.entries())
        void navigate({ to: url.pathname as '/', search: Object.keys(search).length > 0 ? search : undefined })
      }}
    />
  )
}

/** The editor reads the same signed-in map catalogue as the team page. */
function MapEditorRouteWithCatalogue() {
  return <AuthenticatedMapCatalogue><MapEditorRoute /></AuthenticatedMapCatalogue>
}

function MapEditorRoute() {
  const navigate = useNavigate()
  const { state } = useAuth()
  const { teamId } = useParams({ strict: false }) as { teamId: string }
  const { data: groups } = useMyGroups()
  const teams = (groups?.groups ?? []).map(groupToTeam)
  const baseTeam = teams.find((t) => t.id === teamId)
  const mapPins = useGroupMapPins(teamId)
  const huntingGrounds = useGroupHuntingGrounds(teamId)
  // CLUB-528: every kind (line/arrow/trail) — never filtered down to just what
  // the editor can display. `mapLines` doubles as the save's `original`: it
  // must be exactly what was loaded, or buildLineSaveTasks would delete
  // whatever got left out (see saveTeamMap.ts's SAFETY note).
  const { lines: mapLines, isResolved: linesResolved } = useGroupMapLines(teamId)
  // CLUB-676: mirrors mapLines/linesResolved above — see the gate below and
  // GroupPinFolders.isResolved's doc comment for why `original` must never be
  // snapshotted before this query answers.
  const { folders: teamPinFolders, isResolved: foldersResolved } = useGroupPinFolders(teamId)
  const queryClient = useQueryClient()
  // Remounting the editor is how server state that changed under it (a save, an
  // import) reaches its snapshot state — it seeds from `team` once per mount.
  // `restoreView` says whether that remount should also put the map back where
  // the user was looking. A save should: the ground did not move, and being
  // thrown out to the fitted whole-ground view after every save loses the spot
  // being worked on. An import should not: it brings in new geometry, and the
  // fitted view is what shows it.
  const [remount, setRemount] = useState({ count: 0, restoreView: false })
  const remountForSave = () => setRemount((r) => ({ count: r.count + 1, restoreView: true }))
  const remountForImport = () => setRemount((r) => ({ count: r.count + 1, restoreView: false }))
  // The editor's latest view, reported as the user pans and zooms. A ref, not
  // state: nothing here renders from it, and re-rendering the route on every
  // pan would be pure waste.
  const editorViewRef = useRef<MapEditorView | undefined>(undefined)
  // The edit right as it stood when this editor was entered, latched on the
  // first answer and never re-read. See the guard below for why it is not read
  // live.
  const mayEditRef = useRef<boolean | undefined>(undefined)
  // The pins this editor mount was SEEDED with — what the save must diff
  // against. The live `mapPins` is not that: the editor snapshots `team.map`
  // once per mount, while the query keeps refreshing under it. A browser that
  // restored a persisted cache from before a field existed (pin notes, until
  // the cache buster was bumped) mounted the editor on pins without notes; the
  // refetch then brought the notes into the live list, the diff read every
  // noted pin as "note removed", and one Save cleared all of a team's notes.
  // The same mismatch read a pin another member added while the editor was
  // open as deleted. Keyed on the remount counter, the same key the editor is.
  const seededPinsRef = useRef<{ key: number; pins: MapObject[] } | null>(null)

  const team = baseTeam
    ? {
        ...baseTeam,
        map: {
          ...baseTeam.map,
          boundaryAreas: huntingGrounds.boundaryAreas,
          subAreaPolygons: huntingGrounds.subAreaPolygons,
          forbiddenPolygons: huntingGrounds.forbiddenPolygons,
          objects: mapPins,
          lines: mapLines,
          // Team folders only — this route never loads the viewer's private
          // ones, and CLUB-676 filing on this path only ever moves a pin
          // between team folders, so there is nothing here to tag `private`
          // on the way in (contrast TeamDetailRoute above, which merges both
          // collections for display).
          pinFolders: teamPinFolders,
        },
      }
    : undefined

  if (!team) {
    if (groups === undefined) return null
    return <TeamAccessDenied onBack={() => navigate({ to: '/teams' })} />
  }

  // Membership is not enough to be here — this route needs the edit right. The
  // Edit map button is already hidden from a view-only member, so the UI never
  // offers the editor, but the URL is reachable without it: a bookmark, a
  // shared link, the back button, or an administrator demoting someone while
  // their tab sits open. Every write is refused server-side, so nothing they do
  // can be persisted; what the unguarded editor cost them was their time, by
  // letting them move pins and redraw areas and only saying no at the save.
  //
  // The team's read-only Map tab is the honest destination: viewing and
  // printing are theirs, and TeamAccessDenied would claim they have no access
  // to a team they are a member of. `replace` keeps the editor URL out of
  // history, so the back button cannot bounce them straight back into it.
  //
  // Deliberately after the membership check above, and therefore never reached
  // while the query is in flight — a redirect on a not-yet-known role would
  // throw a legitimate editor out of the editor on every cold load.
  //
  // The answer is LATCHED rather than read live, and that is the load-bearing
  // part. The membership query runs staleTime 0 with refetchOnMount 'always'
  // and refetches on reconnect, so a fresh answer can arrive at any moment
  // while someone is mid-drawing. Gating on the live value would then unmount
  // the editor under them and take every unsaved change with it — for a
  // demotion they may not even have had yet, since a network blip is enough to
  // re-ask. Whether a demoted member should be evicted mid-edit is a product
  // decision about discarding their work; it is not a decision worth making by
  // accident, so this gate covers entry only and a role that changes after that
  // takes effect the next time they navigate.
  if (mayEditRef.current === undefined) mayEditRef.current = team.canEdit
  if (!mayEditRef.current) {
    return (
      <Navigate to="/teams/$teamId" params={{ teamId }} search={{ tab: 'map' }} replace />
    )
  }

  // CLUB-528 / CLUB-676: do not mount the editor until BOTH the lines and the
  // pin-folders queries have answered. MapEditorPage snapshots `team.map` into
  // a lazy useState with no prop-sync, and this route only re-keys it on a
  // remount bump — so an editor mounted while either query was still in
  // flight keeps that field's empty default (`lines: []` / `pinFolders: []`,
  // via `team.map` above) forever, even after the real data arrives. The save
  // then diffs that empty snapshot against the by-then-loaded `mapLines` /
  // `teamPinFolders`, and buildLineSaveTasks / saveTeamMap's folder phase
  // correctly read every loaded line, arrow, recorded trail, or FOLDER as
  // deleted — for folders, that means every team folder gets DELETEd on the
  // very first save from an editor that raced this query. Unlike map pins and
  // hunting grounds, nothing warms either query before the editor route, so on
  // in-app navigation the race was the normal case, not the rare one. Gating
  // the mount is what makes an empty `current` against a non-empty `original`
  // unreachable; a guard inside the save could not tell this apart from the
  // user genuinely deleting every line or folder.
  if (!linesResolved || !foldersResolved) return null

  // Captured in the render that mounts (or re-keys) the editor, so it is
  // exactly the list the editor seeds from.
  if (seededPinsRef.current?.key !== remount.count) {
    seededPinsRef.current = { key: remount.count, pins: mapPins }
  }
  const seededPins = seededPinsRef.current.pins

  const handleSave = async (mapState: {
    boundaryAreas: GeoArea[]
    subAreaPolygons: GeoArea[]
    forbiddenPolygons: GeoArea[]
    objects: MapObject[]
    lines: GeoLine[]
    folders?: PinFolder[]
  }) => {
    await saveTeamMap({
      teamId,
      mapState,
      originalAreas: huntingGrounds.rawAreas,
      // What the editor was seeded with, never the live query: see seededPinsRef.
      originalPins: seededPins,
      // CLUB-528: `current` is exactly the payload the editor handed back
      // (every kind it received, whether it can edit that kind or not);
      // `original` is exactly what was loaded — never a re-query — so a line
      // the editor never touched can never look "deleted" by omission.
      lines: { current: mapState.lines, original: mapLines },
      // CLUB-676: same shape and same reasoning as `lines` above. The editor
      // always sends `folders` (see MapEditorPage's onSave doc comment), but
      // it is typed optional there for older callers, hence the `?? []`.
      // `teamPinFolders` is the hook's LIVE value, not a value captured at
      // mount — same as `mapLines` above it — so a background refetch (e.g.
      // `refetchOnReconnect`, on by default) landing between mount and this
      // Save could make `original` newer than what the editor actually
      // snapshotted, the same hazard `lines` carries. The API's refusal to
      // delete a non-empty folder (see saveTeamMap's phase-3 comment) bounds
      // the resulting damage to an EMPTY folder someone else created in that
      // window — anything holding pins survives; nothing here can widen that.
      folders: { current: mapState.folders ?? [], original: teamPinFolders },
      // Resolved per write, not captured for the whole save: a save that is
      // waiting out rate-limit windows runs long enough for a token captured
      // here to expire. Same reasoning as onImportSveaskog below.
      getToken: () => (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken(),
    })
    // Both the refetch and the remount stay on the SUCCESS path, and they have
    // to move together.
    //
    // A partially-failed save is genuinely unreconciled — the writes that
    // landed are creates whose server ids the editor never learns, because no
    // create echoes a client id and this save discards its results. It is
    // tempting to refetch anyway so a second Save does not duplicate them. That
    // is not safe: the editor seeds its snapshot once per mount, so refetching
    // without remounting leaves `original` strictly NEWER than the `current`
    // the editor hands back — and all three builders delete whatever appears in
    // `original` and not in `current`. A trail a hunter recorded on mobile
    // since this editor mounted would be pulled into `original` by the refetch
    // and then deleted by omission on the next save, which is exactly what the
    // SAFETY note on buildLineSaveTasks forbids. Remounting instead of
    // refetching is no better: it rebuilds the editor from server data and
    // discards the edits the failed save left as the only surviving copy.
    //
    // So a failed save leaves both caches at load time and the map dirty. A
    // second Save re-creates what already landed — visible, and recoverable by
    // deleting the duplicates. The real fix is a create that carries a
    // client-minted id the API upserts on, which makes the second save an
    // update instead of a duplicate; see the WC-93 plan doc.
    await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'hunting-grounds'] })
    await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'map-pins'] })
    await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'map-lines'] })
    await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'map-pin-folders'] })
    remountForSave()
  }

  // CLUB-188: the user's other teams are the eligible copy sources. Loading a
  // source's map fetches its boundary/areas/pins on demand from the copy flow.
  const copySources = teams
    .filter((t) => t.id !== teamId)
    .map((t) => ({ id: t.id, name: t.name }))

  const onLoadCopySource = async (sourceTeamId: string) => {
    const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
    return loadCopyableTeamMap(sourceTeamId, token)
  }

  // CLUB-190: import an Oma Riista area by code. On success the fresh hunting
  // grounds are fetched straight away, but the editor is NOT remounted yet.
  //
  // CLUB-523: remounting is how the imported boundary gets on screen (the editor
  // seeds its state once, from `team`), and it is keyed on the remount counter —
  // so bumping it here tore down the import panel the instant the request
  // resolved, before
  // its "Area imported" confirmation could be read. To the user the panel just
  // vanished. The editor now reports when the panel has been dismissed, and the
  // remount happens then.
  const onImportOmaRiista = async (areaCode: string) => {
    const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
    const result = await importOmaRiistaArea(teamId, areaCode, token)
    if (result.status === 'created' || result.status === 'refreshed') {
      await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'hunting-grounds'] })
    }
    return result
  }

  // CLUB-189: import a Sveaskog area by objektnummer. On success the fresh hunting
  // grounds are fetched straight away, but the editor is NOT remounted yet.
  //
  // Mirrors onImportOmaRiista, including why the editor remount is deferred:
  // refetch the grounds now so the data is ready, but leave the remount to
  // onSveaskogImported, which fires when the panel is dismissed. Remounting on
  // resolve would destroy the panel before its confirmation could be read.
  const onImportSveaskog = async (objektnummer: string) => {
    const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
    const result = await importSveaskogArea(teamId, objektnummer, token)
    if (result.status === 'created' || result.status === 'refreshed') {
      await queryClient.refetchQueries({ queryKey: ['clubhouse', 'teams', teamId, 'hunting-grounds'] })
    }
    return result
  }

  // CLUB-191: Reviiri import. Three steps, one handler each. The Firebase token is
  // fetched per call rather than captured once: these calls are separated by however
  // long the user spends typing a password and reading a club list, which is easily
  // long enough for a captured token to expire.
  const getRequiredAuthToken = async () => {
    if (state.status !== 'signed-in') {
      throw new Error('Authentication is required for Reviiri import.')
    }
    return state.user.getIdToken()
  }

  const onReviiriConnect = async (email: string, password: string) => {
    const token = await getRequiredAuthToken()
    return connectReviiri(email, password, token)
  }

  const onLoadReviiriClubs = async () => {
    const token = await getRequiredAuthToken()
    return fetchReviiriClubs(token)
  }

  // Mirrors onImportOmaRiista: refetch the grounds now so the data is ready, but leave
  // the editor remount to onReviiriImported, which fires when the panel is dismissed.
  // Remounting on resolve would destroy the panel before its confirmation is read.
  const onImportReviiri = async (mapId: string) => {
    const token = await getRequiredAuthToken()
    const result = await importReviiriMap(teamId, mapId, token)
    if (result.status === 'created' || result.status === 'refreshed') {
      await queryClient.refetchQueries({
        queryKey: ['clubhouse', 'teams', teamId, 'hunting-grounds'],
      })
    }
    return result
  }

  // The Tanger-backed property overlay + tap-to-select authenticate per-user:
  // the backend forwards this Firebase token as the Tanger `firebase` login.
  const getAuthToken = async () =>
    state.status === 'signed-in' ? await state.user.getIdToken() : null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
      <MapEditorPage
        key={remount.count}
        team={team}
        // What the print server would use for a sheet centred here, so the print
        // surface can draw the frame the server will actually compose. Answered by
        // the Estate API's Tanger passthrough — web never calls Tanger directly.
        // Out of session the lookup rejects: the endpoint is authenticated, and no
        // frame is a better answer than a made-up one.
        getPrintProbe={async (center) => {
          if (state.status !== 'signed-in') throw new Error('print probe: signed out')
          return await fetchPrintProbe(center.lat, center.lon, await state.user.getIdToken())
        }}
        initialView={remount.restoreView ? editorViewRef.current : undefined}
        onViewChange={(view) => { editorViewRef.current = view }}
        onBack={() => navigate({ to: '/teams/$teamId', params: { teamId }, search: { tab: 'map' } })}
        onSave={handleSave}
        copySources={copySources}
        onLoadCopySource={onLoadCopySource}
        onImportOmaRiista={onImportOmaRiista}
        onOmaRiistaImported={remountForImport}
        onImportSveaskog={onImportSveaskog}
        onSveaskogImported={remountForImport}
        onReviiriConnect={onReviiriConnect}
        onLoadReviiriClubs={onLoadReviiriClubs}
        onImportReviiri={onImportReviiri}
        onReviiriImported={remountForImport}
        getAuthToken={getAuthToken}
      />
    </div>
  )
}

function SettingsRoute() {
  const { state } = useAuth()
  const changePassword = useChangePassword()

  // Withheld for an account with no password of its own — see
  // hasPasswordProvider for why offering it would be a dead end. With no
  // handler the Settings page hides the Password row rather than disabling it.
  return (
    <SettingsPage
      onChangePassword={hasPasswordProvider(state) ? changePassword : undefined}
    />
  )
}

function AccountSettingsRoute() {
  const { t } = useTranslation()
  return (
    <AccountEmptyShell
      title={t('routes.account.title')}
      subtitle={t('routes.account.subtitle')}
      icon="settings"
      headline={t('routes.account.emptyHeadline')}
      body={t('routes.account.emptyBody')}
    />
  )
}

function HuntSettingsRoute() {
  const { t } = useTranslation()
  return (
    <AccountEmptyShell
      title={t('routes.huntSettings.title')}
      subtitle={t('routes.huntSettings.subtitle')}
      icon="hunts"
      headline={t('routes.huntSettings.emptyHeadline')}
      body={t('routes.huntSettings.emptyBody')}
    />
  )
}

export function buildClubhouseRoutes<TRoot extends AnyRootRoute>(
  rootRoute: TRoot,
  chronicle: ChronicleSlots,
  features: ClubhouseFeatures = {},
  campfire: CampfireSlots = {},
) {
  // Closes over the injected flags — see the note where PlanHuntRoute used to
  // be declared at module scope.
  function PlanHuntRoute() {
    return <PlanHuntPage subAreaScopeEnabled={features.driveSubAreaScope === true} />
  }

  // A wrapper rather than moving HuntDetailRoute in here: the route body is long,
  // and all it needs from this scope is the injected slot and the resolved flags.
  function HuntDetailRouteWithSlots() {
    return <HuntDetailRoute campfire={campfire} features={features} />
  }

  // Same wrapper shape as HuntDetailRouteWithSlots: the team page's invite button
  // is gated by an injected flag, so it needs the resolved features in scope.
  function TeamDetailRouteWithFeatures() {
    return <AuthenticatedMapCatalogue><TeamDetailRoute features={features} /></AuthenticatedMapCatalogue>
  }

  // Defined here (not module scope) so it closes over the injected chronicle
  // slots — the overview is the one clubhouse view that renders chronicle UI.
  function OverviewRoute() {
    const navigate = useNavigate()
    const { state } = useAuth()
    const uid = state.status === 'signed-in' ? state.user.uid : undefined
    const { data: member } = useMember(uid)
    const { teams: chronicleTeams } = chronicle.useTeams()
    const { data: upcoming } = useUpcomingHunts()
    // CLUB-136: Your Season "Hunts attended" / "Hours in field" derived from the
    // same ended-hunts data the Ended Hunts section uses (single source of truth).
    // Undefined while loading so the season card falls back rather than flashing 0.
    const { data: endedForSeason } = useEndedHunts()
    const seasonStats = endedForSeason ? endedHuntsSeasonStats(endedForSeason.hunts) : undefined

    const userName =
      member?.name ??
      (state.status === 'signed-in' ? state.user.displayName : null) ??
      'Hunter'

    const teams = chronicleTeams.map(chronicleTeamToTeam)
    const teamIds = chronicleTeams.map((t) => t.id)
    const teamsById = Object.fromEntries(chronicleTeams.map((t) => [t.id, t.name]))
    const upcomingHunts = (upcoming?.hunts ?? []).map(upcomingDtoToHunt)

    return (
      <OverviewPage
        user={{ name: userName }}
        teams={teams}
        hunts={upcomingHunts}
        onOpenHunt={(id) => {
          const hunt = upcomingHunts.find((h) => h.id === id)
          void navigate({ to: '/hunts/$id', params: { id }, search: hunt ? huntDetailSearchFor(hunt, '/') : { from: '/' } })
        }}
        topStatsSection={
          <chronicle.TopStats
            teamIds={teamIds}
            teamsById={teamsById}
            onContinueWithTeam={(teamId) =>
              navigate({ to: '/teams/$teamId', params: { teamId } })
            }
          />
        }
        seasonSection={<chronicle.OverviewSection huntsAttended={seasonStats?.huntsAttended} hoursInField={seasonStats?.hoursInField} />}
        activityPanel={<chronicle.ActivityPanel />}
        // CLUB-138 hid the Activity overview panel while its data was incomplete
        // (Hours in field / Participants read 0). Those metrics are now wired to
        // real, range-aware data (#585), so the panel — with its 7/14/30/60/90d
        // range toggles and all four metrics — is surfaced again.
        showActivityPanel={true}
      />
    )
  }

  return [
    createRoute({ getParentRoute: () => rootRoute, path: '/',                  component: OverviewRoute }),
    createRoute({ getParentRoute: () => rootRoute, path: '/hunts',             component: HuntsRoute }),
    createRoute({ getParentRoute: () => rootRoute, path: '/hunts/plan',        component: PlanHuntRoute, validateSearch: (s: Record<string, unknown>) => ({ draftId: typeof s.draftId === 'string' ? s.draftId : undefined, groupId: typeof s.groupId === 'string' ? s.groupId : undefined, date: typeof s.date === 'string' ? s.date : undefined }) }),
    createRoute({ getParentRoute: () => rootRoute, path: '/hunts/$id',         component: HuntDetailRouteWithSlots }),
    createRoute({ getParentRoute: () => rootRoute, path: '/teams',             component: TeamsRoute }),
    createRoute({ getParentRoute: () => rootRoute, path: '/teams/$teamId',     component: TeamDetailRouteWithFeatures }),
    createRoute({ getParentRoute: () => rootRoute, path: '/teams/$teamId/map', component: MapEditorRouteWithCatalogue }),
    createRoute({ getParentRoute: () => rootRoute, path: '/dogs',              component: () => <DogsPage dogs={SAMPLE_DOGS} devices={SAMPLE_DEVICES} /> }),
    createRoute({ getParentRoute: () => rootRoute, path: '/devices',           component: () => <DevicesPage devices={SAMPLE_DEVICES} /> }),
    createRoute({ getParentRoute: () => rootRoute, path: '/settings',          component: SettingsRoute }),
    createRoute({ getParentRoute: () => rootRoute, path: '/settings/account',  component: AccountSettingsRoute }),
    createRoute({ getParentRoute: () => rootRoute, path: '/settings/hunt',     component: HuntSettingsRoute }),
  ]
}
